import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";
import { clp } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";
import { useCatalog } from "./Employees";

const KINDS = [{ id: "bono", name: "Bono (+)" }, { id: "descuento", name: "Descuento (−)" }];
const ROWS = [["Haberes"], ["sueldo", (c) => `Sueldo (${c.dias_pagados} días de ${clp(c.sueldo_base)})`], ["he_monto", (c) => `Horas extra (${c.he_hours} h × ${clp(c.valor_he)})`],
  ["gratificacion", "Gratificación legal"], ["bonos_imp", "Bonos imponibles"], ["imponible", "Total imponible", true], ["bonos_no_imp", "Bonos no imponibles"],
  ["no_imponibles", "Colación y movilización"], ["haberes", "Total haberes", true], ["Descuentos"], ["afp", (c) => `AFP ${c.afp_nombre || "(sin AFP)"} ${c.afp_tasa}%`],
  ["salud", (c) => (c.salud_tipo === "isapre" ? "Isapre" : "Fonasa 7%")], ["cesantia", "Seguro de cesantía 0,6%"], ["impuesto", (c) => `Impuesto único (base ${clp(c.tributable)})`],
  ["otros_descuentos", "Otros descuentos"], ["liquido", "Líquido a pagar", true]];

const Breakdown = ({ c }) => (
  <div className="rounded-xl border border-[#e4e8e1] divide-y divide-[#f0f2ee] text-sm" data-testid="line-breakdown">
    {ROWS.map(([k, l, bold]) => (l === undefined
      ? <div key={k} className="px-3 py-1.5 bg-[#f7f9f5] text-[10px] font-bold uppercase tracking-[0.15em] text-[#5b6656]">{k}</div>
      : <div key={k} className={`flex justify-between px-3 py-1.5 ${bold ? "font-bold" : ""}`}><span>{typeof l === "function" ? l(c) : l}</span><span data-testid={`breakdown-${k}`}>{clp(c[k])}</span></div>))}
  </div>
);

export const LineDialog = ({ run, line, canEdit, onClose, onSaved }) => {
  const concepts = useCatalog("conceptos_planilla");
  const [items, setItems] = useState([]);
  const [adj, setAdj] = useState(0);
  useEffect(() => { setItems(line?.items || []); setAdj(line?.he_adjust || 0); }, [line]);
  const set = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    try {
      const { data } = await api.put(`/rrhh/payroll/runs/${run.id}/lines/${line.employee_id}/items`,
        { items: items.map((x) => ({ ...x, amount: parseInt(x.amount, 10) || 0 })), he_adjust: Number(adj) || 0 });
      toast.success("Cambios guardados"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="line-dialog">
        <DialogHeader><DialogTitle>{line?.name} · {run.period}</DialogTitle></DialogHeader>
        {line && <div className="grid md:grid-cols-2 gap-5">
          <Breakdown c={line.calc} />
          <div className="space-y-4">
            <div className="text-xs text-[#5b6656]">HH.EE. por marcas: <b>{line.he_marks} h</b> · días trabajados: <b>{line.worked_days}</b></div>
            <Field label="Ajuste manual de horas extra (+/− h)"><Input data-testid="line-he-adjust-input" type="number" step="0.5" disabled={!canEdit} value={adj} onChange={(e) => setAdj(e.target.value)} /></Field>
            <div className="font-semibold text-sm">Bonos y descuentos</div>
            {items.map((x, i) => (
              <div key={i} className="space-y-2 rounded-lg border border-[#f0f2ee] p-2" data-testid={`item-row-${i}`}>
                <div className="grid grid-cols-2 gap-2">
                  <Pick testid={`item-kind-${i}`} value={x.kind} onChange={(v) => set(i, "kind", v)} options={KINDS} />
                  <Pick testid={`item-concept-${i}`} value={x.concept} onChange={(v) => set(i, "concept", v)} options={concepts} placeholder="Concepto" />
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid={`item-amount-${i}`} type="number" min="1" disabled={!canEdit} value={x.amount} onChange={(e) => set(i, "amount", e.target.value)} />
                  {x.kind === "bono" && <label className="flex items-center gap-1 text-xs whitespace-nowrap"><Switch data-testid={`item-imponible-${i}`} checked={x.imponible !== false} onCheckedChange={(v) => set(i, "imponible", v)} />Imponible</label>}
                  {canEdit && <button data-testid={`item-remove-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-[#8a9584] hover:text-[#b42318]"><Trash2 size={15} /></button>}
                </div>
              </div>))}
            {canEdit && <Button data-testid="item-add-btn" variant="outline" size="sm" className="rounded-full" onClick={() => setItems([...items, { kind: "bono", concept: "", amount: "", imponible: true, note: "" }])}><Plus size={14} className="mr-1" />Agregar</Button>}
          </div>
        </div>}
        {canEdit && <DialogFooter><Button data-testid="items-save-btn" className="rounded-full" onClick={save}>Guardar y recalcular</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
